import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { 
  Plus, 
  Check, 
  X,
  Clock,
  AlertCircle,
  Pencil,
  Ban
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { fr, de, enUS } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Absences() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const locale = language === 'de' ? de : language === 'en' ? enUS : fr;
  
  const [absences, setAbsences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedAbsence, setSelectedAbsence] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [newAbsence, setNewAbsence] = useState({
    start_date: '',
    end_date: '',
    reason: '',
    absence_type: 'conge',
    day_part: 'full'
  });
  const [editAbsence, setEditAbsence] = useState({
    start_date: '',
    end_date: '',
    reason: '',
    absence_type: 'conge',
    day_part: 'full'
  });
  const [rejectionReason, setRejectionReason] = useState('');

  const isManager = user?.role === 'manager' || user?.role === 'directeur';

  const ABSENCE_TYPES = [
    { value: 'conge', label: t('paidLeave') },
    { value: 'maladie', label: t('sickLeave') },
    { value: 'sans_solde', label: t('unpaidLeave') },
    { value: 'autre', label: t('other') },
  ];

  const DAY_PARTS = [
    { value: 'full', label: t('fullDay') },
    { value: 'morning', label: t('morning') },
    { value: 'afternoon', label: t('afternoon') },
  ];

  const STATUS_LABELS = {
    pending: { label: t('pending'), class: 'bg-amber-500' },
    approved: { label: t('approved'), class: 'bg-emerald-500' },
    rejected: { label: t('rejected'), class: 'bg-red-500' },
    cancelled: { label: t('cancelled'), class: 'bg-gray-500' },
  };

  const fetchAbsences = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      const res = await axios.get(`${API}/absences`, { params });
      setAbsences(res.data);
    } catch (err) {
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, t]);

  useEffect(() => {
    fetchAbsences();
  }, [fetchAbsences]);

  const handleCreateAbsence = async () => {
    try {
      await axios.post(`${API}/absences`, newAbsence);
      toast.success(t('requestCreated'));
      setDialogOpen(false);
      setNewAbsence({
        start_date: '',
        end_date: '',
        reason: '',
        absence_type: 'conge',
        day_part: 'full'
      });
      fetchAbsences();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error'));
    }
  };

  const handleUpdateAbsence = async () => {
    if (!selectedAbsence) return;
    try {
      await axios.put(`${API}/absences/${selectedAbsence.id}`, editAbsence);
      toast.success(t('success'));
      setEditDialogOpen(false);
      setSelectedAbsence(null);
      fetchAbsences();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error'));
    }
  };

  const openEditAbsenceDialog = (absence) => {
    setSelectedAbsence(absence);
    setEditAbsence({
      start_date: absence.start_date,
      end_date: absence.end_date,
      reason: absence.reason,
      absence_type: absence.absence_type,
      day_part: absence.day_part || 'full'
    });
    setEditDialogOpen(true);
  };

  const handleReview = async (status) => {
    if (!selectedAbsence) return;
    
    try {
      await axios.put(`${API}/absences/${selectedAbsence.id}/review`, {
        status,
        rejection_reason: status === 'rejected' ? rejectionReason : (status === 'cancelled' ? t('cancelledByAdmin') : null)
      });
      const messages = {
        approved: t('requestApproved'),
        rejected: t('requestRejected'),
        cancelled: t('requestCancelled')
      };
      toast.success(messages[status] || t('success'));
      setReviewDialogOpen(false);
      setSelectedAbsence(null);
      setRejectionReason('');
      fetchAbsences();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error'));
    }
  };

  const handleCancelAbsence = async (absence) => {
    if (!window.confirm(t('confirmCancelAbsence'))) return;
    
    try {
      await axios.put(`${API}/absences/${absence.id}/review`, {
        status: 'cancelled',
        rejection_reason: t('cancelledByAdmin')
      });
      toast.success(t('requestCancelled'));
      fetchAbsences();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error'));
    }
  };

  const handleDeleteAbsence = async (id) => {
    try {
      await axios.delete(`${API}/absences/${id}`);
      toast.success(t('success'));
      fetchAbsences();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error'));
    }
  };

  const openReviewDialog = (absence) => {
    setSelectedAbsence(absence);
    setReviewDialogOpen(true);
  };

  const pendingCount = absences.filter(a => a.status === 'pending').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="absences-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl">
            {isManager ? t('absenceRequests').toUpperCase() : t('myAbsences').toUpperCase()}
          </h1>
          <p className="text-muted-foreground text-sm">
            {pendingCount} {t('requestsPending')}
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[150px]" data-testid="status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('allMembers')}</SelectItem>
              <SelectItem value="pending">{t('pending')}</SelectItem>
              <SelectItem value="approved">{t('approved')}</SelectItem>
              <SelectItem value="rejected">{t('rejected')}</SelectItem>
              <SelectItem value="cancelled">{t('cancelled')}</SelectItem>
            </SelectContent>
          </Select>
          
          <Button onClick={() => setDialogOpen(true)} data-testid="new-absence-btn">
            <Plus className="h-4 w-4 mr-2" />
            {t('newRequest')}
          </Button>
        </div>
      </div>

      {/* Absences List */}
      {absences.length === 0 ? (
        <div className="border border-border rounded-lg p-8 text-center">
          <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">{t('noAbsences')}</p>
        </div>
      ) : (
        <div className="border border-border rounded-lg overflow-hidden">
          <table className="data-table">
            <thead>
              <tr>
                {isManager && <th>{t('collaborator')}</th>}
                <th>{t('type')}</th>
                <th>{t('period')}</th>
                <th>{t('dayPart')}</th>
                <th>{t('reason')}</th>
                <th>{t('status')}</th>
                <th>{t('actions')}</th>
              </tr>
            </thead>
            <tbody>
              {absences.map((absence) => (
                <tr key={absence.id} data-testid={`absence-row-${absence.id}`}>
                  {isManager && (
                    <td className="font-medium">{absence.user_name}</td>
                  )}
                  <td>
                    {ABSENCE_TYPES.find(t => t.value === absence.absence_type)?.label || absence.absence_type}
                  </td>
                  <td className="mono text-sm">
                    {format(parseISO(absence.start_date), 'dd/MM/yyyy', { locale })}
                    {absence.start_date !== absence.end_date && (
                      <> → {format(parseISO(absence.end_date), 'dd/MM/yyyy', { locale })}</>
                    )}
                  </td>
                  <td className="text-sm">
                    {DAY_PARTS.find(p => p.value === (absence.day_part || 'full'))?.label || t('fullDay')}
                  </td>
                  <td className="max-w-[200px] truncate">{absence.reason}</td>
                  <td>
                    <Badge className={`${STATUS_LABELS[absence.status].class} text-white rounded`}>
                      {STATUS_LABELS[absence.status].label}
                    </Badge>
                  </td>
                  <td>
                    <div className="flex items-center gap-1">
                      {isManager && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openEditAbsenceDialog(absence)}
                          className="h-8 px-2"
                          data-testid={`edit-btn-${absence.id}`}
                          title={t('edit')}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      )}
                      {isManager && absence.status === 'pending' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openReviewDialog(absence)}
                          className="h-8 px-2"
                          data-testid={`review-btn-${absence.id}`}
                        >
                          <Clock className="h-4 w-4" />
                        </Button>
                      )}
                      {isManager && absence.status === 'approved' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleCancelAbsence(absence)}
                          className="h-8 px-2 text-orange-600 hover:text-orange-700"
                          data-testid={`cancel-btn-${absence.id}`}
                          title={t('cancel')}
                        >
                          <Ban className="h-4 w-4" />
                        </Button>
                      )}
                      {absence.user_id === user?.id && absence.status === 'pending' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteAbsence(absence.id)}
                          className="h-8 px-2 text-destructive hover:text-destructive"
                          data-testid={`delete-btn-${absence.id}`}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* New Absence Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('newAbsenceRequest').toUpperCase()}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>{t('absenceType')}</Label>
              <Select
                value={newAbsence.absence_type}
                onValueChange={(v) => setNewAbsence({ ...newAbsence, absence_type: v })}
              >
                <SelectTrigger data-testid="absence-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ABSENCE_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('startDate')}</Label>
                <Input
                  type="date"
                  value={newAbsence.start_date}
                  onChange={(e) => setNewAbsence({ ...newAbsence, start_date: e.target.value, end_date: e.target.value })}
                  data-testid="absence-start-date"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('endDate')}</Label>
                <Input
                  type="date"
                  value={newAbsence.end_date}
                  onChange={(e) => setNewAbsence({ ...newAbsence, end_date: e.target.value })}
                  data-testid="absence-end-date"
                />
              </div>
            </div>

            {/* Day part selection - only show if single day */}
            {newAbsence.start_date && newAbsence.start_date === newAbsence.end_date && (
              <div className="space-y-2">
                <Label>{t('dayPart')}</Label>
                <Select
                  value={newAbsence.day_part}
                  onValueChange={(v) => setNewAbsence({ ...newAbsence, day_part: v })}
                >
                  <SelectTrigger data-testid="absence-day-part">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DAY_PARTS.map(part => (
                      <SelectItem key={part.value} value={part.value}>
                        {part.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label>{t('reason')}</Label>
              <Textarea
                value={newAbsence.reason}
                onChange={(e) => setNewAbsence({ ...newAbsence, reason: e.target.value })}
                placeholder={t('reasonPlaceholder')}
                data-testid="absence-reason"
              />
            </div>

            <Button onClick={handleCreateAbsence} className="w-full" data-testid="submit-absence-btn">
              {t('submitRequest')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('processRequest').toUpperCase()}</DialogTitle>
          </DialogHeader>
          
          {selectedAbsence && (
            <div className="space-y-4 pt-4">
              <div className="border border-border rounded-lg p-4 space-y-2 bg-muted/30">
                <p><span className="text-muted-foreground">{t('collaborator')}:</span> {selectedAbsence.user_name}</p>
                <p><span className="text-muted-foreground">{t('type')}:</span> {ABSENCE_TYPES.find(t => t.value === selectedAbsence.absence_type)?.label}</p>
                <p>
                  <span className="text-muted-foreground">{t('period')}:</span>{' '}
                  {format(parseISO(selectedAbsence.start_date), 'dd/MM/yyyy')} → {format(parseISO(selectedAbsence.end_date), 'dd/MM/yyyy')}
                </p>
                <p><span className="text-muted-foreground">{t('reason')}:</span> {selectedAbsence.reason}</p>
              </div>

              <div className="space-y-2">
                <Label>{t('rejectionReason')}</Label>
                <Textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder={t('rejectionPlaceholder')}
                  data-testid="rejection-reason"
                />
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={() => handleReview('approved')}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  data-testid="approve-btn"
                >
                  <Check className="h-4 w-4 mr-2" />
                  {t('approve')}
                </Button>
                <Button
                  onClick={() => handleReview('rejected')}
                  variant="destructive"
                  className="flex-1"
                  data-testid="reject-btn"
                >
                  <X className="h-4 w-4 mr-2" />
                  {t('reject')}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Absence Dialog (Admin only) */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('edit').toUpperCase()} - {t('absenceRequests')}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>{t('absenceType')}</Label>
              <Select
                value={editAbsence.absence_type}
                onValueChange={(v) => setEditAbsence({ ...editAbsence, absence_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ABSENCE_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('startDate')}</Label>
                <Input
                  type="date"
                  value={editAbsence.start_date}
                  onChange={(e) => setEditAbsence({ ...editAbsence, start_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('endDate')}</Label>
                <Input
                  type="date"
                  value={editAbsence.end_date}
                  onChange={(e) => setEditAbsence({ ...editAbsence, end_date: e.target.value })}
                />
              </div>
            </div>

            {editAbsence.start_date && editAbsence.start_date === editAbsence.end_date && (
              <div className="space-y-2">
                <Label>{t('dayPart')}</Label>
                <Select
                  value={editAbsence.day_part}
                  onValueChange={(v) => setEditAbsence({ ...editAbsence, day_part: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DAY_PARTS.map(part => (
                      <SelectItem key={part.value} value={part.value}>
                        {part.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label>{t('reason')}</Label>
              <Textarea
                value={editAbsence.reason}
                onChange={(e) => setEditAbsence({ ...editAbsence, reason: e.target.value })}
              />
            </div>

            <Button onClick={handleUpdateAbsence} className="w-full">
              {t('update')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
